import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { authService } from '../services/authService';

interface User {
  id: string;
  email: string | null;
  role?: string;
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar_url?: string;
}

interface UserState {
  user: User | null;
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
}

interface UserActions {
  setUser: (user: User | null) => void;
  setIsLoading: (loading: boolean) => void;
  loadProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  signOut: () => Promise<void>;
}

type UserStore = UserState & UserActions;

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        profile: null,
        isLoading: false,
        error: null,

        setUser: (user) => {
          console.debug('%cUserStore setUser', 'color: orange', {
            previousUser: get().user,
            newUser: user
          });
          set({ user });
        },
        setIsLoading: (loading) => set({ isLoading: loading }),

        loadProfile: async () => {
          const { user } = get();
          if (!user) return;

          set({ isLoading: true });
          try {
            const { profile, error } = await authService.getProfile(user.id);
            if (error) throw error;
            set({ profile });
          } catch (error) {
            set({ error: 'Failed to load profile' });
          } finally {
            set({ isLoading: false });
          }
        },

        updateProfile: async (updates) => {
          const { user } = get();
          if (!user) return;

          set({ isLoading: true });
          try {
            const { error } = await authService.updateProfile(updates);
            if (error) throw error;
            set((state) => ({
              profile: state.profile ? { ...state.profile, ...updates } : null
            }));
          } catch (error) {
            set({ error: 'Failed to update profile' });
          } finally {
            set({ isLoading: false });
          }
        },

        signOut: async () => {
          set({ isLoading: true });
          try {
            const { error } = await authService.signOut();
            if (error) throw error;
            set({ user: null, profile: null });
          } catch (error) {
            set({ error: 'Failed to sign out' });
          } finally {
            set({ isLoading: false });
          }
        }
      }),
      {
        name: 'user-storage',
        partialize: (state) => ({ user: state.user, profile: state.profile })
      }
    )
  )
); 